/**
 * Photo upload pass — runs after the text-submission drain. For every
 * synced FieldRecord submission with photos_pending=true, uploads the
 * blobs to /api/v1/data-vault/sampling/field-records/{server_id}/photos
 * and deletes them from photo_blobs.
 *
 * Failure modes:
 *   - Network error → leave photos in place, retry on next pass.
 *   - 4xx error     → log + delete. Likely a content-type/size issue
 *                     server-side; keeping the photo around won't help.
 *   - 410/404 on parent → server lost the FieldRecord; clear
 *                     photos_pending so we don't loop, log audit event.
 *
 * One photo at a time per record, sequential. Field photos are small
 * (~300 KB) and sites typically have ≤3 photos per FieldRecord, so
 * parallelism isn't worth the complexity.
 */
import { apiClient, HttpError } from '../api/client';
import { getDb, type PhotoBlobRow } from '../db';
import { logEvent } from './log';

export async function uploadPendingPhotos(): Promise<void> {
  const db = getDb();
  // Find synced FieldRecord submissions that still have photos.
  const candidates = await db.pending_submissions
    .filter((row) => row.status === 'synced' && row.photos_pending === true)
    .toArray();

  for (const row of candidates) {
    if (!row.server_id) {
      // Marked photos_pending without a server_id — defensive cleanup.
      await db.pending_submissions.update(row.client_submission_id, {
        photos_pending: false,
      });
      continue;
    }
    const photos = await db.photo_blobs
      .where('client_submission_id')
      .equals(row.client_submission_id)
      .toArray();
    if (photos.length === 0) {
      // No photos left to upload (e.g. user deleted them after submit).
      await db.pending_submissions.update(row.client_submission_id, {
        photos_pending: false,
      });
      continue;
    }

    let parent_alive = true;
    for (const photo of photos) {
      if (!parent_alive) break;
      const result = await uploadOne(row.server_id, photo);
      if (result === 'ok') {
        await db.photo_blobs.delete(photo.key);
      } else if (result === 'parent_gone') {
        await logEvent('photo_upload.parent_gone', {
          server_id: row.server_id,
          photo_key: photo.key,
        });
        // Don't loop forever waiting on a record the server doesn't have.
        parent_alive = false;
      } else if (result === 'reject') {
        // Server rejected this specific photo — drop it so we don't
        // retry forever. The user's other photos may still upload.
        await db.photo_blobs.delete(photo.key);
      } // 'retry' → leave in place; next pass picks it up.
    }

    // Recheck after the loop. If photos all uploaded (or were dropped),
    // clear the flag.
    const remaining = await db.photo_blobs
      .where('client_submission_id')
      .equals(row.client_submission_id)
      .count();
    if (remaining === 0 || !parent_alive) {
      await db.pending_submissions.update(row.client_submission_id, {
        photos_pending: false,
      });
    }
  }
}

type UploadOutcome = 'ok' | 'retry' | 'reject' | 'parent_gone';

async function uploadOne(
  server_id: string,
  photo: PhotoBlobRow,
): Promise<UploadOutcome> {
  const form = new FormData();
  // Field name: 'file' (matches the FastAPI handler from Phase 2.13).
  // Use a stable filename so server logs include something meaningful.
  const ext = photo.blob.type === 'image/png' ? 'png' : 'jpg';
  form.append('file', photo.blob, `${photo.key}.${ext}`);
  if (photo.taken_at != null) {
    form.append('taken_at', new Date(photo.taken_at).toISOString());
  }
  if (photo.gps_lat != null) form.append('gps_lat', String(photo.gps_lat));
  if (photo.gps_lon != null) form.append('gps_lon', String(photo.gps_lon));
  if (photo.gps_accuracy_m != null) {
    form.append('gps_accuracy_m', String(photo.gps_accuracy_m));
  }
  if (photo.width_px != null) form.append('width_px', String(photo.width_px));
  if (photo.height_px != null) form.append('height_px', String(photo.height_px));

  try {
    await apiClient.postForm(
      `/api/v1/data-vault/sampling/field-records/${server_id}/photos`,
      form,
    );
    await logEvent('photo_upload.ok', {
      server_id,
      photo_key: photo.key,
      size_bytes: photo.blob.size,
    });
    return 'ok';
  } catch (err) {
    if (err instanceof HttpError) {
      if (err.status === 404 || err.status === 410) return 'parent_gone';
      if (err.status >= 400 && err.status < 500) {
        await logEvent('photo_upload.reject', {
          server_id,
          photo_key: photo.key,
          status: err.status,
        });
        return 'reject';
      }
    }
    return 'retry';
  }
}
